/* eslint-disable @typescript-eslint/no-explicit-any */
// utils/apiClient.ts

import { refreshAccessToken } from './refreshAccessToken';
import { getBaseApi } from './runtimeOrg';
import { formatApiError } from './formatApiError';
import { getProfessionalErrorMessage } from './errorHandling';

interface ApiClientOptions extends RequestInit {
  headers?: HeadersInit;
  body?: any;
}

export interface ApiError extends Error {
  status: number;
  response: unknown;
  fieldErrors: Record<string, string>;
}

function isAbsoluteHttpUrl(u?: string) {
  return !!u && /^https?:\/\//i.test(u);
}

function joinUrl(base: string, path: string): string {
  if (!base) return path;
  if (isAbsoluteHttpUrl(path)) return path;
  const b = base.replace(/\/+$/, '');
  const p = String(path || '').replace(/^\/+/, '');
  return `${b}/${p}`;
}

function buildApiError(status: number, data: unknown): ApiError {
  const { message, fieldErrors } = formatApiError(data);
  const fallback = `Request failed (${status})`;
  // Sanitize HTML/stack traces but preserve the raw API text so engineers can
  // debug from the UI alone. See utils/errorHandling/getProfessionalErrorMessage.
  const cleaned = getProfessionalErrorMessage(message || fallback);
  const err = new Error(cleaned) as ApiError;
  err.status = status;
  err.response = data;
  err.fieldErrors = fieldErrors;
  return err;
}

function isFormDataBody(body: any): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

function isFileOrBlob(body: any): body is File | Blob {
  return (
    (typeof File !== 'undefined' && body instanceof File) ||
    (typeof Blob !== 'undefined' && body instanceof Blob)
  );
}

export const apiClient = async (
  endpoint: string,
  { method = 'GET', headers = {}, body, ...customConfig }: ApiClientOptions = {}
) => {
  //  Use baseApi directly, no /api/ prefix
  const baseApi = getBaseApi(); // absolute URL, no trailing slash
  const url = joinUrl(baseApi, endpoint);

  const readAccessToken = () =>
    (typeof window !== 'undefined'
      ? localStorage.getItem('accessToken')
      : undefined) ?? undefined;

  const buildConfig = (token?: string): RequestInit => {
    const h = new Headers(headers);
    if (!h.has('Accept')) h.set('Accept', 'application/json');

    let finalBody: any = body;

    if (isFormDataBody(finalBody)) {
      h.delete('Content-Type'); // browser sets boundary
    } else if (isFileOrBlob(finalBody)) {
      const fd = new FormData();
      fd.append('file', finalBody);
      finalBody = fd;
      h.delete('Content-Type');
    } else if (
      finalBody &&
      typeof finalBody === 'object' &&
      typeof finalBody !== 'string'
    ) {
      if (!h.has('Content-Type')) h.set('Content-Type', 'application/json');
      finalBody = JSON.stringify(finalBody);
    } else if (typeof finalBody === 'string') {
      if (!h.has('Content-Type'))
        h.set('Content-Type', 'text/plain;charset=UTF-8');
    }

    if (token) h.set('Authorization', `Bearer ${token}`);

    const init: RequestInit = {
      method: method.toUpperCase(),
      headers: h,
      ...customConfig,
    };

    if (
      method.toUpperCase() !== 'GET' &&
      method.toUpperCase() !== 'HEAD' &&
      finalBody != null
    ) {
      (init as any).body = finalBody;
    }

    return init;
  };

  const doFetch = (token?: string) => fetch(url, buildConfig(token));

  let token = readAccessToken();
  let response = await doFetch(token);

  // handle unauthorized (401) once
  if (response.status === 401) {
    const snapshot = await response
      .clone()
      .json()
      .catch(() => null as any);
    const tokenLikelyInvalid =
      snapshot?.code === 'token_not_valid' ||
      snapshot?.detail === 'Given token not valid for any token type';

    if (tokenLikelyInvalid) {
      try {
        const newToken = await refreshAccessToken();
        if (newToken) {
          if (typeof window !== 'undefined') {
            localStorage.setItem('accessToken', String(newToken));
          }
          token = String(newToken);
          response = await doFetch(token);
        } else {
          throw new Error('no_token');
        }
      } catch {
        // refresh failed → sign out & retry once without token
        try {
          const [{ store }, modPerform] = await Promise.all([
            import('@/redux/store'),
            import('@/redux/auth/signin/performSignOut'),
          ]);
          const performSignOut = (modPerform as any).performSignOut;
          if (performSignOut && (store as any)?.dispatch) {
            await performSignOut()((store as any).dispatch);
          } else if (typeof window !== 'undefined') {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('username');
            localStorage.removeItem('userProfile');
          }
        } catch {
          try {
            if (typeof window !== 'undefined') {
              localStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
            }
          } catch {}
        }
        response = await doFetch(undefined);
      }
    }
  }

  const contentType = response.headers.get('Content-Type') || '';
  const isJson = contentType.includes('application/json');
  const data = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    throw buildApiError(response.status, data);
  }

  return data;
};
